import React, { useEffect, useState } from 'react';
import {
  Chip, CircularProgress, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Link } from 'react-router-dom';

import { searchStudents } from '../../actions/journal';
import { journalGroupLink } from '../../utils/consts/pathRoutes';
import { formatDate, studentCardLink, studentName } from '../../utils/journalFormat';
import useIsMobile from '../../utils/useIsMobile';

const MIN_LENGTH = 2;
const DEBOUNCE_MS = 350;

/**
 * Поиск слушателя на экране «Списки групп».
 *
 * Главный сценарий заказчика — по входящему официальному запросу срочно найти человека
 * и его куратора. Поиск идёт отдельным лёгким запросом к журналу; общий список
 * контингента (~90 колонок без пагинации) для этого не годится.
 */
const StudentSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    const search = query.trim();

    if (search.length < MIN_LENGTH) {
      setResults(null);
      return undefined;
    }

    // Пауза перед запросом: сервер однопроцессорный, дёргать его на каждую букву незачем.
    const timer = setTimeout(() => {
      setLoading(true);
      searchStudents(search)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div style={{ marginBottom: 24 }}>
      <TextField
        label="Найти слушателя по ФИО"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        {...(isMobile ? { fullWidth: true } : { sx: { minWidth: 340 } })}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: '#8a8a8a' }} fontSize="small" />,
          endAdornment: loading ? <CircularProgress size={16} color="warning" /> : null,
        }}
        helperText={query.trim().length > 0 && query.trim().length < MIN_LENGTH
          ? `Введите не меньше ${MIN_LENGTH} символов`
          : ' '}
      />

      {/* На телефоне найденные — карточками: из шести колонок без прокрутки
          видно только ФИО, а ради куратора поиск и затевался. */}
      {results !== null && isMobile && (
        results.length === 0 ? (
          <Paper variant="outlined" className="journal-card journal-muted">
            Слушатели не найдены. Возможно, он ещё не распределён в группу.
          </Paper>
        ) : (
          <div className="journal-cards">
            {results.map((row) => (
              <Paper
                key={row.membership_id} variant="outlined" className="journal-card"
                sx={{ opacity: row.is_active ? 1 : 0.55 }}
              >
                <div className="journal-card__head">
                  <div>
                    <Link className="journal-link" to={studentCardLink(row)}>
                      {studentName(row)}
                    </Link>
                    {row.russian_name && (
                      <div className="journal-muted">{row.russian_name}</div>
                    )}
                  </div>
                  <Chip size="small" label={row.status} />
                </div>

                <div className="journal-card__meta">
                  <Link className="journal-link" to={journalGroupLink(row.group_id)}>
                    {row.group_name}
                  </Link>
                  {' · '}{row.semester} семестр{row.country ? ` · ${row.country}` : ''}
                </div>
                <div className="journal-card__meta">Куратор: {row.curator_name || '—'}</div>
                <div className="journal-muted" style={{ marginTop: 4 }}>
                  {formatDate(row.date_from)}
                  {row.date_to ? ` — ${formatDate(row.date_to)}` : ''}
                </div>
              </Paper>
            ))}
          </div>
        )
      )}

      {results !== null && !isMobile && (
        <Paper sx={{ mt: 1, maxWidth: 1100 }}>
          {results.length === 0 ? (
            <div className="journal-muted" style={{ padding: 16 }}>
              Слушатели не найдены. Возможно, он ещё не распределён в группу.
            </div>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Слушатель</TableCell>
                  <TableCell>Страна</TableCell>
                  <TableCell>Группа</TableCell>
                  <TableCell>Куратор</TableCell>
                  <TableCell>Период</TableCell>
                  <TableCell>Статус</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row) => (
                  <TableRow key={row.membership_id} hover sx={{ opacity: row.is_active ? 1 : 0.55 }}>
                    <TableCell>
                      <Link className="journal-link" to={studentCardLink(row)}>
                        {studentName(row)}
                      </Link>
                      {row.russian_name && (
                        <div className="journal-muted">{row.russian_name}</div>
                      )}
                    </TableCell>
                    <TableCell>{row.country || '—'}</TableCell>
                    <TableCell>
                      <Link className="journal-link" to={journalGroupLink(row.group_id)}>
                        {row.group_name}
                      </Link>
                      <div className="journal-muted">{row.semester} семестр</div>
                    </TableCell>
                    <TableCell>{row.curator_name || '—'}</TableCell>
                    <TableCell>
                      {formatDate(row.date_from)}
                      {row.date_to ? ` — ${formatDate(row.date_to)}` : ''}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={row.status} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </div>
  );
};

export default StudentSearch;
